// src/App.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink as RouterNavLink } from "react-router-dom";
import Dashboard from "./components/Dashboard";
import ScreenerView from "./components/ScreenerView";
import PortfolioView from "./components/PortfolioView";
import HealthView from "./components/HealthView";
import SettingsView from "./components/SettingsView";
import AnomalyView from "./components/AnomalyView";
import SignalsView from "./components/SignalsView";
import BacktestView from "./components/BacktestView";
import PaperPositionsView from "./components/PaperPositionsView";
import SimulationDashboard from "./components/SimulationDashboard";
import GettingStartedView from "./components/GettingStartedView";
import { getProviderKeysStatus } from "./api/settings";
import { getAppSurfacePolicy } from "./api/settings";
import { getLegalDisclaimerPolicy } from "./api/settings";
import { createCheckoutSession } from "./api/billing";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { Alert, AppBar, Box, Button, IconButton, Link, Stack, Toolbar, Typography } from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import ExploreIcon from "@mui/icons-material/Explore";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import Badge from "@mui/material/Badge";
import LockIcon from "@mui/icons-material/Lock";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReadinessProvider } from "./context/ReadinessContext";
import { CapabilitiesProvider, useCapabilities } from "./context/CapabilitiesContext";
import { ColorModeContext } from "./context/ColorModeContext";
import { buildTheme } from "./theme";

const NAV_LINKS = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/setup", label: "Setup", badge: true },
  { to: "/screener", label: "Screener", allowedTiers: ["advanced", "pro", "executive"] },
  { to: "/paper-positions", label: "Paper Positions", allowedTiers: ["pro", "executive"] },
  { to: "/simulation", label: "Simulation", allowedTiers: ["pro", "executive"] },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/signals", label: "Signals", allowedTiers: ["advanced", "pro", "executive"] },
  { to: "/backtest", label: "Backtest", allowedTiers: ["pro", "executive"] },
  { to: "/anomaly", label: "Anomaly", allowedTiers: ["pro", "executive"] },
  { to: "/health", label: "Health" },
  { to: "/settings", label: "Settings" },
];

const DEFAULT_APP_SURFACE_POLICY = {
  disclaimer_not_financial_advice:
    "TradePilot is for informational purposes only and does not constitute financial advice.",
  risk_disclosure_summary:
    "TradePilot results are algorithmically generated research outputs. Past scores, rankings, and backtests do not guarantee future performance.",
  legal_links: {
    terms_of_service: "https://trytradepilot.com/terms",
    privacy_policy: "https://trytradepilot.com/privacy",
    risk_disclosure: "https://trytradepilot.com/risk-disclosure",
  },
  ops_baseline: {
    support_email: "admin@trytradepilot.com",
    security_email: "admin@trytradepilot.com",
  },
};

// Tier-aware nav buttons — must render inside CapabilitiesProvider
function NavButtons({ missingKeys, isMobile }) {
  const { activeTier, loading: tierLoading } = useCapabilities();
  return (
    <>
      {NAV_LINKS.map(({ to, label, end, badge, allowedTiers }) => {
        const isLocked = !tierLoading && allowedTiers && !allowedTiers.includes(activeTier);
        return (
          <Button
            key={to}
            component={RouterNavLink}
            to={to}
            end={end}
            size="small"
            sx={{
              color: isLocked ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.72)",
              fontWeight: 400,
              textTransform: "none",
              fontSize: isMobile ? "0.72rem" : "0.8rem",
              px: isMobile ? 1 : 1.25,
              py: isMobile ? 0.5 : 0.75,
              minWidth: "auto",
              borderRadius: 1,
              whiteSpace: "nowrap",
              bgcolor: "transparent",
              "&.active": {
                color: isLocked ? "rgba(255,255,255,0.35)" : "#ffffff",
                fontWeight: 600,
                bgcolor: isLocked ? "transparent" : "rgba(255,255,255,0.12)",
              },
              "&:hover": {
                bgcolor: isLocked ? "transparent" : "rgba(255,255,255,0.08)",
                color: isLocked ? "rgba(255,255,255,0.35)" : "#fff",
                cursor: isLocked ? "default" : "pointer",
              },
            }}
          >
            {badge && missingKeys ? (
              <Badge color="warning" variant="dot" sx={{ "& .MuiBadge-dot": { top: 4, right: 2 } }}>
                {label}
              </Badge>
            ) : isLocked ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LockIcon sx={{ fontSize: 11 }} />
                {label}
              </Box>
            ) : label}
          </Button>
        );
      })}
    </>
  );
}

// Route-level tier gate — renders upgrade prompt instead of view when tier is insufficient
function GatedRoute({ element, allowedTiers, requiredTier }) {
  const { activeTier, loading } = useCapabilities();
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState("");
  const [billingInterval, setBillingInterval] = useState("monthly");

  const tierSlug = requiredTier ? requiredTier.toLowerCase() : null;

  const handleUpgrade = useCallback(async () => {
    if (!tierSlug) return;
    setUpgrading(true);
    setUpgradeError("");
    try {
      const { url } = await createCheckoutSession({ tier: tierSlug, interval: billingInterval });
      window.location.href = url;
    } catch {
      setUpgradeError("Could not start checkout. Please try again or contact support.");
      setUpgrading(false);
    }
  }, [tierSlug, billingInterval]);

  const handleIntervalChange = (_event, nextValue) => {
    if (!nextValue) return;
    setBillingInterval(nextValue);
  };

  if (loading) return null;
  if (!allowedTiers || allowedTiers.includes(activeTier)) return element;
  return (
    <Box sx={{ maxWidth: 480, mx: "auto", mt: 8, px: 2, textAlign: "center" }}>
      <LockIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {requiredTier} plan required
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This section is available on {requiredTier} and above.
      </Typography>
      {upgradeError && (
        <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>{upgradeError}</Alert>
      )}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Choose billing interval
        </Typography>
        <ToggleButtonGroup
          color="primary"
          exclusive
          size="small"
          value={billingInterval}
          onChange={handleIntervalChange}
          aria-label="billing interval"
        >
          <ToggleButton value="monthly" aria-label="monthly billing">Monthly</ToggleButton>
          <ToggleButton value="annual" aria-label="annual billing">Annual</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
        {tierSlug && (
          <Button
            variant="contained"
            disabled={upgrading}
            onClick={handleUpgrade}
          >
            {upgrading ? "Opening checkout…" : `Upgrade to ${requiredTier}`}
          </Button>
        )}
        <Button variant="outlined" component={RouterNavLink} to="/setup">
          View plan details
        </Button>
      </Box>
    </Box>
  );
}

const App = () => {
  const [mode, setMode] = useState("light");
  const [missingKeys, setMissingKeys] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [appSurfacePolicy, setAppSurfacePolicy] = useState(DEFAULT_APP_SURFACE_POLICY);

  const colorMode = useMemo(
    () => ({ toggleColorMode: () => setMode((prev) => (prev === "light" ? "dark" : "light")) }),
    []
  );
  const theme = useMemo(() => buildTheme(mode), [mode]);
  const activeTheme = useTheme();
  const isMobile = useMediaQuery(activeTheme.breakpoints.down("sm"));

  useEffect(() => {
    let isMounted = true;

    const refreshProviderStatus = () => {
      Promise.resolve()
        .then(() => getProviderKeysStatus())
        .then((status) => {
          if (!isMounted) return;
          const alpacaOk = Boolean(status?.alpaca?.configured);
          const anyOk = Object.values(status || {}).some((v) => v?.configured);
          setMissingKeys(!alpacaOk && !anyOk);
        })
        .catch(() => {
          if (!isMounted) return;
          setMissingKeys(false);
        });
    };

    refreshProviderStatus();

    const onFocus = () => refreshProviderStatus();
    window.addEventListener("focus", onFocus);

    return () => {
      isMounted = false;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    Promise.allSettled([getAppSurfacePolicy(), getLegalDisclaimerPolicy()])
      .then(([surfaceResult, legalResult]) => {
        const surfacePolicy =
          surfaceResult?.status === "fulfilled" && surfaceResult.value && typeof surfaceResult.value === "object"
            ? surfaceResult.value
            : {};
        const legalPolicy =
          legalResult?.status === "fulfilled" && legalResult.value && typeof legalResult.value === "object"
            ? legalResult.value
            : {};

        if (!isMounted) return;

        setAppSurfacePolicy({
          ...DEFAULT_APP_SURFACE_POLICY,
          ...surfacePolicy,
          ...legalPolicy,
          legal_links: {
            ...DEFAULT_APP_SURFACE_POLICY.legal_links,
            ...(surfacePolicy.legal_links || {}),
            ...(legalPolicy.legal_links || {}),
          },
          ops_baseline: {
            ...DEFAULT_APP_SURFACE_POLICY.ops_baseline,
            ...(surfacePolicy.ops_baseline || {}),
            ...(legalPolicy.ops_baseline || {}),
          },
        });
      })
      .catch(() => {
        if (isMounted) {
          setAppSurfacePolicy(DEFAULT_APP_SURFACE_POLICY);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const disclaimer = appSurfacePolicy?.disclaimer_not_financial_advice || DEFAULT_APP_SURFACE_POLICY.disclaimer_not_financial_advice;
  const riskDisclosureSummary = appSurfacePolicy?.risk_disclosure_summary || DEFAULT_APP_SURFACE_POLICY.risk_disclosure_summary;
  const legalLinks = appSurfacePolicy?.legal_links || DEFAULT_APP_SURFACE_POLICY.legal_links;
  const opsBaseline = appSurfacePolicy?.ops_baseline || DEFAULT_APP_SURFACE_POLICY.ops_baseline;

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <CapabilitiesProvider>
            <ReadinessProvider>
              {/* Top nav bar */}
              <AppBar position="sticky" elevation={0}>
                <Toolbar
                  variant="dense"
                  sx={{
                    gap: isMobile ? 0.75 : 1,
                    minHeight: isMobile ? "auto" : 52,
                    px: { xs: 1.5, sm: 2 },
                    py: isMobile ? 0.75 : 0,
                    alignItems: isMobile ? "stretch" : "center",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                  }}
                >
                  {/* Brand */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      width: isMobile ? "100%" : "auto",
                      mr: isMobile ? 0 : 2,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component={RouterNavLink}
                      to="/"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                        textDecoration: "none",
                      }}
                    >
                      <ExploreIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.9)", transform: "rotate(-45deg)" }} />
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ color: "#fff", letterSpacing: "-0.25px", lineHeight: 1, userSelect: "none" }}
                      >
                        TradePilot
                      </Typography>
                    </Box>

                    {/* Dark mode toggle */}
                    <IconButton
                      onClick={colorMode.toggleColorMode}
                      size="small"
                      sx={{ color: "rgba(255,255,255,0.85)", flexShrink: 0, ml: isMobile ? 1 : 0 }}
                      aria-label="toggle dark mode"
                    >
                      {mode === "dark" ? (
                        <Brightness7Icon fontSize="small" />
                      ) : (
                        <Brightness4Icon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Nav links */}
                  <Box
                    component="nav"
                    sx={{
                      display: "flex",
                      gap: 0,
                      flex: 1,
                      width: isMobile ? "100%" : "auto",
                      flexBasis: isMobile ? "100%" : "auto",
                      minWidth: 0,
                      overflowX: isMobile ? "visible" : "auto",
                      overflowY: "hidden",
                      flexWrap: isMobile ? "wrap" : "nowrap",
                      justifyContent: isMobile ? "flex-start" : "flex-start",
                      scrollBehavior: "smooth",
                      rowGap: isMobile ? 0.5 : 0,
                      "&::-webkit-scrollbar": isMobile ? {} : { display: "none" },
                      scrollbarWidth: isMobile ? "auto" : "none",
                    }}
                  >
                    <NavButtons missingKeys={missingKeys} isMobile={isMobile} />
                  </Box>
                </Toolbar>
              </AppBar>

        {missingKeys && !bannerDismissed && (
          <Box sx={{ mx: 2, mt: 1 }}>
            <Alert
              severity="warning"
              onClose={() => setBannerDismissed(true)}
            >
              No provider keys configured. Visit the{" "}
              <RouterNavLink to="/setup" style={{ fontWeight: 600 }}>
                Setup guide
              </RouterNavLink>{" "}
              to get started — it only takes a few minutes.
            </Alert>
          </Box>
        )}

        <Box sx={{ mx: 2, mt: 1 }}>
          <Alert severity="info" icon={false} sx={{ "& .MuiAlert-message": { width: "100%" } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {disclaimer}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {riskDisclosureSummary}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Link href={legalLinks.terms_of_service} target="_blank" rel="noreferrer" underline="hover" color="inherit">
                  Terms
                </Link>
                <Link href={legalLinks.privacy_policy} target="_blank" rel="noreferrer" underline="hover" color="inherit">
                  Privacy
                </Link>
                <Link href={legalLinks.risk_disclosure} target="_blank" rel="noreferrer" underline="hover" color="inherit">
                  Risk Disclosure
                </Link>
              </Stack>
            </Stack>
          </Alert>
        </Box>

              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/screener" element={<GatedRoute element={<ScreenerView />} allowedTiers={["advanced", "pro", "executive"]} requiredTier="Advanced" />} />
                <Route path="/paper-positions" element={<GatedRoute element={<PaperPositionsView />} allowedTiers={["pro", "executive"]} requiredTier="Pro" />} />
                <Route path="/simulation" element={<GatedRoute element={<SimulationDashboard />} allowedTiers={["pro", "executive"]} requiredTier="Pro" />} />
                <Route path="/anomaly" element={<GatedRoute element={<AnomalyView />} allowedTiers={["pro", "executive"]} requiredTier="Pro" />} />
                <Route path="/portfolio" element={<PortfolioView />} />
                <Route path="/signals" element={<GatedRoute element={<SignalsView />} allowedTiers={["advanced", "pro", "executive"]} requiredTier="Advanced" />} />
                <Route path="/backtest" element={<GatedRoute element={<BacktestView />} allowedTiers={["pro", "executive"]} requiredTier="Pro" />} />
                <Route path="/setup" element={<GettingStartedView />} />
                <Route path="/health" element={<HealthView />} />
                <Route path="/settings" element={<SettingsView />} />
              </Routes>

              <Box
                component="footer"
                sx={{
                  mt: 4,
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.secondary">
                    {riskDisclosureSummary}
                  </Typography>
                  <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                    <Link href={legalLinks.terms_of_service} target="_blank" rel="noreferrer" underline="hover" color="inherit">
                      Terms
                    </Link>
                    <Link href={legalLinks.privacy_policy} target="_blank" rel="noreferrer" underline="hover" color="inherit">
                      Privacy
                    </Link>
                    <Link href={legalLinks.risk_disclosure} target="_blank" rel="noreferrer" underline="hover" color="inherit">
                      Risk Disclosure
                    </Link>
                    <Link href={`mailto:${opsBaseline.support_email}`} underline="hover" color="inherit">
                      Support
                    </Link>
                    <Link href={`mailto:${opsBaseline.security_email}`} underline="hover" color="inherit">
                      Security
                    </Link>
                  </Stack>
                </Stack>
              </Box>
            </ReadinessProvider>
          </CapabilitiesProvider>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
